import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Navigation, UserCircle, MessageCircle, ShieldOff } from 'lucide-react';
import { sos as sosApi } from '@/lib/trackizy';
import { showToast } from '@/components/shared/Toast';
import useFeatures from '@/hooks/useFeatures';

const MENU_WIDTH = 220;
const MENU_HEIGHT_ESTIMATE = 220;

/**
 * Right-click menu for staff phones — both the marker on the map and the
 * row in the side panel. v1 actions:
 *   - Zoom to: opens Google Maps directions to the last fix coords
 *   - View in People: navigates to /people (preselected user is a future polish)
 *   - Chat: navigates to /chat?user=<id> to start/open a direct conversation
 *     (only with the Chat module — /chat is FeatureGate'd and would redirect)
 *   - Clear SOS: when there's an active SOS, acknowledge it server-side
 *
 * Future actions deferred to Phase 5:
 *   - Start replay (people route replay not yet built)
 */
export default function StaffContextMenu({ row, x, y, onClose, onZoomTo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { chat: chatEnabled } = useFeatures();
  const menuRef = useRef(null);

  const clampedX = Math.min(x, window.innerWidth - MENU_WIDTH - 8);
  const clampedY = Math.min(y, window.innerHeight - MENU_HEIGHT_ESTIMATE - 8);

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    function onKeyDown(e) { if (e.key === 'Escape') handleClose(); }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  useEffect(() => {
    function onScroll() { handleClose(); }
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [handleClose]);

  useEffect(() => {
    function onPointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) handleClose();
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [handleClose]);

  const hasFix = row?.lastFix?.latitude != null && row?.lastFix?.longitude != null;
  const googleMapsUrl = hasFix
    ? `https://www.google.com/maps/dir/?api=1&destination=${row.lastFix.latitude},${row.lastFix.longitude}`
    : null;

  const hasSos = !!row?.sos?.id;

  const actions = [
    {
      icon: Navigation,
      label: t('contextMenu.zoomTo', 'Zoom to'),
      disabled: !hasFix,
      onClick: () => {
        if (onZoomTo && hasFix) {
          onZoomTo({ latitude: row.lastFix.latitude, longitude: row.lastFix.longitude });
        } else if (googleMapsUrl) {
          window.open(googleMapsUrl, '_blank');
        }
        handleClose();
      },
    },
    {
      icon: UserCircle,
      label: t('contextMenu.viewInPeople', 'View in People'),
      onClick: () => {
        navigate('/people');
        handleClose();
      },
    },
    chatEnabled && {
      icon: MessageCircle,
      label: t('contextMenu.chat', 'Chat'),
      disabled: !row?.userId,
      onClick: () => {
        if (row?.userId) navigate(`/chat?user=${row.userId}`);
        handleClose();
      },
    },
    {
      icon: ShieldOff,
      label: t('contextMenu.clearSos', 'Clear SOS'),
      disabled: !hasSos,
      onClick: async () => {
        if (!hasSos) return handleClose();
        try {
          await sosApi.acknowledge(row.sos.id);
          showToast(t('contextMenu.sosCleared', 'SOS acknowledged'));
        } catch (err) {
          showToast(t('common.failed', 'Failed') + ': ' + (err?.message ?? err));
        }
        handleClose();
      },
    },
  ].filter(Boolean);

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[220px] bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-xl shadow-xl dark:shadow-black/50 border border-slate-200/60 dark:border-slate-700/60 py-1 overflow-hidden"
      style={{ left: clampedX, top: clampedY }}
    >
      <div className="px-3 py-2 border-b border-slate-200/60 dark:border-slate-700/60">
        <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate text-start">
          {row?.name ?? '—'}
        </p>
        <p className="text-[10px] text-slate-500 capitalize">
          {(row?.staffRole ?? 'other').replace(/_/g, ' ')}
        </p>
      </div>
      <div className="py-1">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            disabled={action.disabled}
            onClick={action.onClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-start text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <action.icon size={15} className="flex-shrink-0 text-slate-500 dark:text-slate-400" />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}
